import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dumbbell } from "lucide-react";

// If the user landed via an invite link (?squad_code=XYZ / ?code=XYZ on /squad
// / ?invite_rank=...&from=...) we stash the squad code in localStorage from
// the landing page. After successful signup we redeem it here so the new user
// joins the inviter's squad immediately.
async function redeemInviteIfAny() {
    try {
        const raw = localStorage.getItem("hg_invite_squad");
        if (!raw) return null;
        const { code } = JSON.parse(raw);
        if (!code) return null;
        await api.post("/squad/join", { invite_code: code });
        localStorage.removeItem("hg_invite_squad");
        return code;
    } catch (e) {
        // Squad join may fail (full, invalid code, etc.) — don't break signup
        return null;
    }
}

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);

    const handleGoogle = () => {
        const redirect = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(form.email, form.password, form.name);
            const joined = await redeemInviteIfAny();
            if (joined) toast.success(`Welcome to Hypergains · joined squad ${joined}`);
            else toast.success("Welcome to Hypergains. 7-day trial active.");
            navigate("/onboarding");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
                <div className="flex items-center justify-center p-8 lg:order-2">
                    <div className="w-full max-w-md">
                        <h1 className="font-display text-3xl font-black uppercase">Start your 7-day trial</h1>
                        <p className="mt-2 text-sm text-muted-foreground">Full access. No card required.</p>
                        <button onClick={handleGoogle} data-testid="register-google" className="mt-6 w-full rounded-sm border border-border bg-card px-4 py-3 text-sm font-bold uppercase tracking-wider hover:bg-secondary">
                            Continue with Google
                        </button>
                        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
                            <div className="h-px flex-1 bg-border" /> or email <div className="h-px flex-1 bg-border" />
                        </div>
                        <form onSubmit={submit} className="space-y-3" data-testid="register-form">
                            <div>
                                <label className="label-eyebrow">Name</label>
                                <input data-testid="register-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm focus:border-accent-red focus:outline-none" />
                            </div>
                            <div>
                                <label className="label-eyebrow">Email</label>
                                <input data-testid="register-email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm focus:border-accent-red focus:outline-none" />
                            </div>
                            <div>
                                <label className="label-eyebrow">Password</label>
                                <input data-testid="register-password" type="password" minLength={6} required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="mt-1 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm focus:border-accent-red focus:outline-none" />
                            </div>
                            <button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-sm bg-accent-red px-4 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] disabled:opacity-60">
                                {loading ? "Creating…" : "Create account"}
                            </button>
                        </form>
                        <p className="mt-6 text-center text-sm text-muted-foreground">
                            Already a member? <Link to="/login" className="font-bold text-accent-red hover:underline" data-testid="register-signin-link">Sign in</Link>
                        </p>
                    </div>
                </div>
                <div className="relative hidden overflow-hidden bg-[#0a0a0a] text-white lg:block lg:order-1">
                    <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1734630341082-0fec0e10126c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)" }} />
                    <div className="absolute inset-0 bg-[#0a0a0a]/70" />
                    <div className="relative flex h-full flex-col justify-between p-10">
                        <Link to="/" className="flex items-center gap-2">
                            <Dumbbell className="h-6 w-6 text-accent-red" />
                            <span className="font-display text-2xl font-black uppercase">Hyper<span className="text-accent-red">gains</span></span>
                        </Link>
                        <div>
                            <div className="label-eyebrow mb-3 text-accent-red">7 days. Full access.</div>
                            <p className="font-display text-4xl font-black uppercase leading-tight">Log your first session.<br />Start the progression.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
